/**
 * TokenUsagePanel — right-rail per-stage LLM token breakdown.
 *
 * S-197 / FEAT-026. Reads the existing GET /pieces/:id/token-usage endpoint
 * (FEAT-024 / S-192), which returns one aggregated row per (stage, model) in
 * pipeline order. A piece that hasn't run yet returns `[]` → empty state.
 */
import { stageLabel } from "@/lib/stage-labels";
import { useApiQuery } from "@/lib/api";

interface StageTokenSummary {
  stage: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  created_at: string | null;
}

const fmt = (n: number) => n.toLocaleString();

interface Props {
  pieceId: number;
}

export function TokenUsagePanel({ pieceId }: Props) {
  const { data, isLoading } = useApiQuery<StageTokenSummary[]>(
    ["piece-token-usage", pieceId],
    `/pieces/${pieceId}/token-usage`
  );

  if (isLoading) {
    return (
      <div className="panel-body">
        <div className="panel-header">
          <h3>Tokens</h3>
          <span className="panel-sub">Per-stage LLM usage</span>
        </div>
        <div className="panel-empty">Loading…</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="panel-body">
        <div className="panel-header">
          <h3>Tokens</h3>
          <span className="panel-sub">Per-stage LLM usage</span>
        </div>
        <div className="panel-empty">
          No token data yet. Run the pipeline to see usage.
        </div>
      </div>
    );
  }

  const totalInput = data.reduce((s, r) => s + r.input_tokens, 0);
  const totalOutput = data.reduce((s, r) => s + r.output_tokens, 0);
  const totalAll = data.reduce((s, r) => s + r.total_tokens, 0);

  return (
    <div className="panel-body">
      <div className="panel-header">
        <h3>Tokens</h3>
        <span className="panel-sub">Per-stage LLM usage</span>
      </div>

      <table className="token-table">
        <thead>
          <tr>
            <th>Stage</th>
            <th>Model</th>
            <th className="token-num">Input</th>
            <th className="token-num">Output</th>
            <th className="token-num">Total</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i}>
              <td>{stageLabel(row.stage)}</td>
              <td className="token-model">{row.model}</td>
              <td className="token-num">{fmt(row.input_tokens)}</td>
              <td className="token-num">{fmt(row.output_tokens)}</td>
              <td className="token-num token-total">{fmt(row.total_tokens)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="token-totals-row">
            <td>Total</td>
            <td className="token-model">—</td>
            <td className="token-num">{fmt(totalInput)}</td>
            <td className="token-num">{fmt(totalOutput)}</td>
            <td className="token-num token-total">{fmt(totalAll)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
